// A runaway self-rescheduling action saturates the main thread's task queue, and the browser
// schedules its rendering update on that same queue — so once nothing yields, `requestAnimationFrame`
// stops firing and the window paints nothing at all while its DOM, its timers and its scripts stay
// perfectly alive. Measured on production: a `loadConfig` handler re-armed with a delay `setTimeout`
// clamps to zero ran ~8000 iterations per second, holding the loop for over a second at a time.
//
// That failure is invisible from the inside: no error is thrown, no request fails, and the page
// reports itself visible. This watchdog exists so the next one is obvious within minutes instead of
// costing a night of bisecting. It only ever logs — recovering automatically is deliberately out of
// scope, because a reload does not repair a rendering pipeline the browser has already given up on,
// and any threshold low enough to catch the real thing also catches an idle machine.
//
// `requestAnimationFrame` is NOT the signal here: it legitimately stops for a sleeping display, a
// running screensaver, a locked machine and an occluded window, none of which change
// `visibilityState`. Timer lateness keeps working through all of them.

const CHECK_INTERVAL = 1000;
// Lateness above this means the loop had no idle moment to run the tick on time. Ordinary work — a
// heavy render, switching chats — can spike here, which is why a single late tick means nothing.
const LATE_THRESHOLD = 500;
// A suspended tab (backgrounded, machine asleep) resumes its timers arbitrarily late. That is not
// saturation, and reporting it would bury the real signal.
const SUSPEND_THRESHOLD = 10000;
// Consecutive late ticks before reporting, so only a sustained stall is worth a line in the console.
const LATE_TICKS_TO_REPORT = 3;

let lateTicks = 0;
let worstLateness = 0;
let isReported = false;
let isSetUp = false;

export default function setupEventLoopWatchdog() {
  if (isSetUp || typeof window === 'undefined') return;
  isSetUp = true;

  let expectedAt = Date.now() + CHECK_INTERVAL;

  window.setInterval(() => {
    const now = Date.now();
    const lateness = now - expectedAt;
    expectedAt = now + CHECK_INTERVAL;

    // A hidden tab has its timers throttled on purpose, so nothing here is measurable
    if (document.visibilityState !== 'visible' || lateness > SUSPEND_THRESHOLD) {
      reset();
      return;
    }

    if (lateness < LATE_THRESHOLD) {
      if (isReported) {
        // eslint-disable-next-line no-console
        console.warn(`[Watchdog] Event loop recovered (worst lateness ${worstLateness}ms)`);
      }
      reset();
      return;
    }

    lateTicks++;
    worstLateness = Math.max(worstLateness, lateness);

    if (lateTicks >= LATE_TICKS_TO_REPORT && !isReported) {
      isReported = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[Watchdog] Event loop saturated for ${lateTicks}s (lateness ${lateness}ms, worst `
        + `${worstLateness}ms). Rendering updates are starved — the page may stop painting. `
        + 'Look for an action rescheduling itself with a non-positive delay.',
      );
    }
  }, CHECK_INTERVAL);
}

function reset() {
  lateTicks = 0;
  worstLateness = 0;
  isReported = false;
}
